import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../../utils/apiConfig';
import './MainAccountRecords.css';

/**
 * 主账户记录列表组件
 * 
 * @param {Object} props
 * @param {Function} props.onRefresh - 刷新回调
 */
const MainAccountRecords = ({ courtId, onRefresh }) => {
  const [records, setRecords] = useState([]);
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    operationType: '',
    startDate: '',
    endDate: ''
  });
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editFormData, setEditFormData] = useState(null);

  const baseUrl = getApiBaseUrl();

  // 获取场地列表
  useEffect(() => {
    const fetchCourts = async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/court-usage/courts`);
        if (response.data.success) {
          setCourts(response.data.data || []);
        }
      } catch (err) {
        console.error('获取场地列表失败:', err);
      }
    };
    fetchCourts();
  }, []);

  // 获取记录列表
  const fetchRecords = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        courtId,
        page,
        limit: pagination.limit,
        ...filters
      };
      
      // 移除空值
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await axios.get(`${baseUrl}/api/court-usage/main-account/records`, { params });
      
      if (response.data.success) {
        setRecords(response.data.data.records || []);
        setPagination({
          page: response.data.data.page,
          limit: response.data.data.limit,
          total: response.data.data.total,
          totalPages: response.data.data.totalPages
        });
      } else {
        throw new Error(response.data.message || '获取记录失败');
      }
    } catch (err) {
      console.error('获取记录失败:', err);
      setError(err.response?.data?.message || err.message || '获取记录失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (courtId) {
      fetchRecords(1);
    }
  }, [filters, courtId]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    fetchRecords(newPage);
  };

  const handleEdit = (record) => {
    setSelectedRecord(record);
    setEditFormData({
      courtId: record.courtId?._id || record.courtId || '',
      operationDate: record.operationDate ? new Date(record.operationDate).toISOString().split('T')[0] : '',
      rechargeAmount: record.rechargeAmount || '',
      courtTime: record.operationType === 'RECHARGE' ? Math.abs(record.operationAmount) : '',
      participants: Array.isArray(record.participants) ? record.participants.join(',') : '',
      description: record.description || ''
    });
    setShowEditForm(true);
  };

  const handleDelete = (record) => {
    setSelectedRecord(record);
    setShowDeleteConfirm(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const updateData = {
        operationDate: editFormData.operationDate,
        description: editFormData.description
      };

      if (selectedRecord.operationType === 'RECHARGE') {
        if (editFormData.rechargeAmount) {
          updateData.rechargeAmount = parseFloat(editFormData.rechargeAmount);
        }
        if (editFormData.courtTime) {
          updateData.courtTime = parseFloat(editFormData.courtTime);
        }
      } else if (selectedRecord.operationType === 'CONSUME') {
        if (editFormData.courtTime) {
          updateData.courtTime = parseFloat(editFormData.courtTime);
        }
        if (editFormData.participants) {
          updateData.participants = editFormData.participants.split(/[,，]/).map(p => p.trim()).filter(p => p);
        }
      }

      if (editFormData.courtId && editFormData.courtId !== selectedRecord.courtId?._id) {
        updateData.courtId = editFormData.courtId;
      }

      const response = await axios.put(
        `${baseUrl}/api/court-usage/main-account/records/${selectedRecord._id}`,
        updateData
      );

      if (response.data.success) {
        setShowEditForm(false);
        setSelectedRecord(null);
        setEditFormData(null);
        fetchRecords(pagination.page);
        onRefresh();
      } else {
        throw new Error(response.data.message || '更新失败');
      }
    } catch (err) {
      console.error('更新失败:', err);
      alert(err.response?.data?.message || err.message || '更新失败');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      const response = await axios.delete(
        `${baseUrl}/api/court-usage/main-account/records/${selectedRecord._id}`
      );

      if (response.data.success) {
        setShowDeleteConfirm(false);
        setSelectedRecord(null);
        fetchRecords(pagination.page);
        onRefresh();
      } else {
        throw new Error(response.data.message || '删除失败');
      }
    } catch (err) {
      console.error('删除失败:', err);
      alert(err.response?.data?.message || err.message || '删除失败');
    } finally {
      setLoading(false);
    }
  };

  const getOperationTypeLabel = (type) => {
    const labels = {
      'RECHARGE': '充值',
      'CONSUME': '消费',
      'ALLOCATE': '分配'
    };
    return labels[type] || type;
  };

  const getOperationTypeClass = (type) => {
    const classes = {
      'RECHARGE': 'recharge',
      'CONSUME': 'consume',
      'ALLOCATE': 'allocate'
    };
    return classes[type] || '';
  };

  return (
    <div className="main-account-records">
      {/* 筛选器 */}
      <div className="filters-container">
        <div className="filter-group">
          <label>操作类型</label>
          <select
            value={filters.operationType}
            onChange={(e) => handleFilterChange('operationType', e.target.value)}
          >
            <option value="">全部</option>
            <option value="RECHARGE">充值</option>
            <option value="CONSUME">消费</option>
            <option value="ALLOCATE">分配</option>
          </select>
        </div>

        <div className="filter-group">
          <label>开始日期</label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>结束日期</label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
          />
        </div>
      </div>

      {/* 记录列表 */}
      {loading && records.length === 0 ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>加载中...</p>
        </div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : records.length === 0 ? (
        <div className="empty-state">
          <p>暂无记录</p>
        </div>
      ) : (
        <>
          <div className="records-list">
            {records.map((record) => (
              <div key={record._id} className={`record-card ${getOperationTypeClass(record.operationType)}`}>
                <div className="record-header">
                  <div className="record-type">
                    <span className={`type-badge ${getOperationTypeClass(record.operationType)}`}>
                      {getOperationTypeLabel(record.operationType)}
                    </span>
                    <span className="record-date">
                      {new Date(record.operationDate).toLocaleDateString('zh-CN')}
                    </span>
                  </div>
                  <div className="record-actions">
                    <button
                      className="edit-button"
                      onClick={() => handleEdit(record)}
                      title="编辑"
                    >
                      编辑
                    </button>
                    <button
                      className="delete-button"
                      onClick={() => handleDelete(record)}
                      title="删除"
                    >
                      删除
                    </button>
                  </div>
                </div>
                
                <div className="record-body">
                  <div className="record-info">
                    <div className="info-row">
                      <span className="info-label">场地:</span>
                      <span className="info-value">{record.courtId?.name || '未知'}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">操作数量:</span>
                      <span className={`info-value ${record.operationAmount > 0 ? 'positive' : 'negative'}`}>
                        {record.operationAmount > 0 ? '+' : ''}{record.operationAmount.toFixed(2)} 场时
                      </span>
                    </div>
                    {record.operationType === 'RECHARGE' && record.rechargeAmount && (
                      <div className="info-row">
                        <span className="info-label">充值金额:</span>
                        <span className="info-value">{record.rechargeAmount} 元</span>
                      </div>
                    )}
                    {record.operationType === 'CONSUME' && record.participants && record.participants.length > 0 && (
                      <div className="info-row">
                        <span className="info-label">参与人员:</span>
                        <span className="info-value">{record.participants.join(', ')}</span>
                      </div>
                    )}
                    {record.description && (
                      <div className="info-row">
                        <span className="info-label">说明:</span>
                        <span className="info-value">{record.description}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="record-balance">
                    <div className="balance-row">
                      <span>操作前: {record.balanceBefore.toFixed(2)}</span>
                      <span>→</span>
                      <span>操作后: {record.balanceAfter.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* 分页 */}
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                className="page-button"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                上一页
              </button>
              <span className="page-info">
                第 {pagination.page} / {pagination.totalPages} 页（共 {pagination.total} 条）
              </span>
              <button
                className="page-button"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
              >
                下一页
              </button>
            </div>
          )}
        </>
      )}

      {/* 编辑表单 */}
      {showEditForm && selectedRecord && editFormData && (
        <div className="modal-overlay" onClick={() => setShowEditForm(false)}>
          <div className="modal-content form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>编辑记录</h2>
              <button className="close-button" onClick={() => setShowEditForm(false)}>×</button>
            </div>
            
            <form onSubmit={handleEditSubmit} className="edit-form">
              <div className="form-group">
                <label>场地</label>
                <select
                  value={editFormData.courtId}
                  onChange={(e) => setEditFormData({ ...editFormData, courtId: e.target.value })}
                >
                  {courts.map(court => (
                    <option key={court._id} value={court._id}>
                      {court.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>操作日期</label>
                <input
                  type="date"
                  value={editFormData.operationDate}
                  onChange={(e) => setEditFormData({ ...editFormData, operationDate: e.target.value })}
                  required
                />
              </div>

              {selectedRecord.operationType === 'RECHARGE' && (
                <>
                  <div className="form-group">
                    <label>充值金额（元）</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={editFormData.rechargeAmount}
                      onChange={(e) => setEditFormData({ ...editFormData, rechargeAmount: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>场时数</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={editFormData.courtTime}
                      onChange={(e) => setEditFormData({ ...editFormData, courtTime: e.target.value })}
                    />
                  </div>
                </>
              )}

              {selectedRecord.operationType === 'CONSUME' && (
                <>
                  <div className="form-group">
                    <label>消费场时数</label>
                    <input
                      type="number"
                      step="0.5"
                      min="0.5"
                      value={editFormData.courtTime}
                      onChange={(e) => setEditFormData({ ...editFormData, courtTime: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>参与人员</label>
                    <input
                      type="text"
                      value={editFormData.participants}
                      onChange={(e) => setEditFormData({ ...editFormData, participants: e.target.value })}
                      placeholder="多个人员用逗号分隔"
                    />
                  </div>
                </>
              )}

              <div className="form-group">
                <label>说明</label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  rows="3"
                />
              </div>

              <div className="form-actions">
                <button type="button" className="cancel-button" onClick={() => setShowEditForm(false)}>
                  取消
                </button>
                <button type="submit" className="submit-button" disabled={loading}>
                  {loading ? '保存中...' : '保存'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 删除确认对话框 */}
      {showDeleteConfirm && selectedRecord && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content delete-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>确认删除</h2>
              <button className="close-button" onClick={() => setShowDeleteConfirm(false)}>×</button>
            </div>
            <div className="delete-confirm-content">
              <p>确定要删除这条记录吗？</p>
              <p className="warning-text">
                删除后将重新计算后续记录的余额，此操作不可恢复。
              </p>
            </div>
            <div className="form-actions">
              <button type="button" className="cancel-button" onClick={() => setShowDeleteConfirm(false)}>
                取消
              </button>
              <button type="button" className="delete-confirm-button" onClick={handleDeleteConfirm} disabled={loading}>
                {loading ? '删除中...' : '确认删除'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainAccountRecords;

